import * as Sentry from '@sentry/nextjs';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import App, { type AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { END } from 'redux-saga';
import { ThemeProvider } from 'styled-components';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import {
  type BearerToken,
  getPersistedToken,
  isTokenExpired,
  persistToken,
  shouldAdoptServerToken,
} from '@koala/sdk/v4';
import { AppCrashFallback } from '@/components/app/app-crash-fallback';
import { MetaDefault } from '@/components/meta/default';
import { MetaSite } from '@/components/meta/site';
import { RouteHandler } from '@/components/routeHandler';
import { ERROR_MESSAGES } from '@/constants/events';
import {
  DEFAULT_SSR_CACHE_POLICY,
  PRODUCTION_HEAP_ID,
  SMB_DRIVER_ID,
  STAGING_HEAP_ID,
} from '@/constants/global';
import { getConfigs } from '@/features/configs/configs';
import { getOrganization } from '@/features/configs/organization';
import { getStrings } from '@/features/configs/strings';
import { useDispatch, useSelector } from '@/redux';
import basketActions from '@/redux/basket/actions';
import cmsConfigActions from '@/redux/cmsConfig/actions';
import meActions from '@/redux/me/actions';
import organizationActions from '@/redux/organization/actions';
import { type SagaStore, wrapper } from '@/redux/store';
import { useGlobalDispatch } from '@/redux/global/reducer';
import { ENV } from '@/constants/envConfig';
import { getOrigin } from '@/utils';
import * as ErrorReporter from '@/utils/errorReporter';
import { prepareErrorMessage, sessionStorageBasket } from '@/utils/global';
import { getGoogleFontsCssUrl, maybeReloadGoogleFonts } from '@/utils/google-fonts';
import { identifyUser } from '@/utils/auth';
import globalActions from '@/redux/global/actions';
import { ensureAppClientToken, getClientToken } from '@/services/client';
import { SESSION_STORAGE_KEYS } from '@/constants/storageKeys';
import GenericErrorBoundary from '@/components/genericErrorBoundary';
import { initChunkLoadErrorHandler } from '@/utils/chunkLoadErrorHandler';
import { createQueryCache } from '@/utils/queryCacheReporting';
import { sentryLogger } from '@/utils/sentryLogger';
import { restoreSavedCartIntoSession } from '@/utils/savedCart';

/**
 * A custom `App` component that wraps every page in the application.
 *
 * @see https://nextjs.org/docs/advanced-features/custom-app
 */
const OrderingApp = ({ Component, pageProps, origin }: AppProps & { origin: string }) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { displayToast } = useGlobalDispatch();

  // Get the brand config from the store.
  const initialConfig = useSelector((state) => state.app.cmsConfig.webConfig);
  const organizationState = useSelector((state) => state.app.organization);
  const { organization } = organizationState;
  const { token } = useSelector((state) => state.app.global);
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Reports guarded reads (e.g. the menu) once, after their last retry. (TEAM-15682)
        queryCache: createQueryCache(),
        defaultOptions: {
          queries: {
            retry: false,
          },
        },
      }),
  );
  const user = useSelector((state) => state.app.me.data);
  const locations = useSelector((state) => state.app.locations.list);
  const captchaSiteKey = organizationState?.external_auth?.captcha?.site_key ?? '';
  const driverId = organization?.ordering_driver_id;

  // Hand the server-rendered client token to the SDK client, which reads it from storage. It also
  // replaces a stored client token that has expired, so a returning guest's first requests don't go
  // out with the stale one and collect 401s (TEAM-15325).
  if (token && shouldAdoptServerToken(getPersistedToken({ origin }), token)) {
    persistToken({ origin, token });
  }

  /* Add Heap script to DOM */
  const loadAndSetScript = (innerHtml: string, position: HTMLElement | null, id = 'heap') => {
    if (!position) {
      return;
    }

    const script = document.createElement('script');
    script.setAttribute('async', '');
    script.setAttribute('id', id);
    script.type = 'text/javascript';
    script.innerHTML = innerHtml;
    position.appendChild(script);
  };

  /* Check if Heap has already loaded. If not, add to DOM  */
  if (typeof window !== 'undefined' && driverId === SMB_DRIVER_ID && ENV !== 'dev') {
    if (!document.querySelector('#heap')) {
      loadAndSetScript(
        `window.heap=window.heap||[],heap.load=function(e,t){window.heap.appid=e,window.heap.config=t=t||{};var r=document.createElement("script");r.type="text/javascript",r.async=!0,r.src="https://cdn.heapanalytics.com/js/heap-"+e+".js";var a=document.getElementsByTagName("script")[0];a.parentNode.insertBefore(r,a);for(var n=function(e){return function(){heap.push([e].concat(Array.prototype.slice.call(arguments,0)))}},p=["addEventProperties","addUserProperties","clearEventProperties","identify","resetIdentity","removeEventProperty","setEventProperties","track","unsetEventProperty"],o=0;o<p.length;o++)heap[p[o]]=n(p[o])};
        heap.load(${origin.includes('staging.chowly.io') ? STAGING_HEAP_ID : PRODUCTION_HEAP_ID});`,
        document.querySelector('head'),
      );
    }
  }

  if (user?.id && ENV !== 'dev') {
    // track user data through Heap on each new login || user reload
    try {
      identifyUser(user, Number(user.id), locations);
    } catch (err) {
      Sentry.captureMessage('Heap blocked: Ad blockers are being used.');
    }
  }

  // Set up error reporting.
  useEffect(() => {
    ErrorReporter.initialize(organization?.label);
    if (organization?.id) {
      sentryLogger.setOrganizationContext(organization);
    }
  }, [organization?.label]);

  // Detect chunk-load failures and auto-reload once per session.
  useEffect(() => {
    return initChunkLoadErrorHandler();
  }, []);

  // Whenever the config slice is updated, refetch google fonts
  useEffect(() => {
    const googleFontUrl = getGoogleFontsCssUrl(initialConfig);

    if (googleFontUrl) {
      maybeReloadGoogleFonts(googleFontUrl);
    }
  }, [initialConfig]);

  useEffect(
    function hydrateClientState() {
      sessionStorage.setItem(SESSION_STORAGE_KEYS.ORDER_START, new Date().toISOString());
      // TEAM-15276: a cart saved with "Save Cart for Later" in a tab that has since closed. Seeds
      // this tab's session basket (only when it has none), so it must run before the hydrate below.
      restoreSavedCartIntoSession();
      // If a basket exists in sessionStorage check that it's is still fresh and valid
      dispatch(basketActions.initializeSessionStorageBasket());
      // check logged-in status
      dispatch(meActions.fetchMe());
    },
    [router.pathname, dispatch],
  );

  useEffect(() => {
    function handleWindowFocus() {
      // If the user has an order and has been inactive for more than 2 hours, the order_session will be reset.
      const hasPreviousOrder = sessionStorage.getItem(SESSION_STORAGE_KEYS.KOALA_ORDER);
      const sessionBasket = sessionStorageBasket.get();
      if (hasPreviousOrder && !sessionBasket) {
        dispatch(displayToast('Your previous order has expired.'));
        dispatch(basketActions.destroyBasket());
      }
    }
    window.addEventListener('focus', handleWindowFocus);
    return () => {
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, []);

  return (
    <>
      <GenericErrorBoundary fallbackUI={<AppCrashFallback />}>
        <MetaSite config={initialConfig} organization={organization} origin={origin} />
        <MetaDefault config={initialConfig} />
        <ThemeProvider theme={initialConfig}>
          <GoogleReCaptchaProvider reCaptchaKey={captchaSiteKey}>
            <QueryClientProvider client={queryClient}>
              <RouteHandler />
              <Component {...pageProps} />
            </QueryClientProvider>
          </GoogleReCaptchaProvider>
        </ThemeProvider>
      </GenericErrorBoundary>
    </>
  );
};

/**
 * Fetching the brand config is a blocking data requirement for every page,
 * so we need to opt-out of Next's automatic static optimization and run
 * the following dispatch on every page.
 */
OrderingApp.getInitialProps = wrapper.getInitialAppProps<string>((store) => async (context) => {
  /**
   * 🚨 REDUX SAGAS TRIGGERED SERVER-SIDE THAT CALL THE SDK WILL NOT WORK!
   * All SDK-dependant Sagas rely on `typeof window !== "undefined"` in
   * order to determine the request origin. This condition won't be met
   * server-side and the sagas will fail silently.
   *
   * If you need to make SDK requests server-side, move the fetch logic
   * directly into this function for now!
   *
   * @see https://github.com/Chowly/koala-ordering-webapp/pull/1786
   */
  const queryClient = new QueryClient();
  let origin: string;
  if (typeof window !== 'undefined') {
    origin = getOrigin(window.location.host);
  } else if (context.ctx.req?.headers.host) {
    origin = getOrigin(context.ctx.req.headers.host);
  } else {
    throw new Error('Missing request origin');
  }

  /**
   * Turn away a host that is not a brand before anything below asks coo-api for a token
   * (TEAM-15326). Ending the response here also stops Next before `_document`, which requests its
   * own token. The `typeof window` guard lets Next compile the server-only module out of the
   * client bundle; see docs/STOREFRONT_HOSTNAME_ALLOWLIST.md.
   */
  if (typeof window === 'undefined') {
    const { isStorefrontHost, sendStorefrontNotFound } = await import(
      '@/server/storefrontHostnames'
    );
    if (!(await isStorefrontHost(origin))) {
      if (context.ctx.res) {
        sendStorefrontNotFound(context.ctx.res);
      }
      store.dispatch(END);
      return { pageProps: {}, origin };
    }
  }

  let token: BearerToken | null | undefined;
  // Handle CSR
  if (typeof window !== 'undefined') {
    token = getPersistedToken({ origin }) ?? store.getState().app.global.token;
    if (token && isTokenExpired(token)) {
      token = null;
    }
    if (!token) {
      token = await ensureAppClientToken({ origin });
    }
  }

  // Handle SSR
  if (typeof window === 'undefined') {
    token = store.getState().app.global.token;
    if (!token) {
      token = await getClientToken({ origin });
      store.dispatch(globalActions.setToken(token));
    }
  }

  try {
    const organization = await getOrganization(queryClient, origin, token);
    // @ts-expect-error
    store.dispatch(organizationActions.fetchOrganizationSuccess(organization));
    const organizationId = (organization?.organization?.id ?? null) as number | null;

    // Fetch strings, and webConfig.
    const [webConfig, strings] = await Promise.all([
      getConfigs(queryClient, origin, organizationId, token),
      getStrings(queryClient, origin, organizationId, token),
    ]);

    // Store success responses
    store.dispatch(cmsConfigActions.fetchConfigsSuccess('fetchServer', 'webConfig', webConfig));
    store.dispatch(cmsConfigActions.fetchConfigsSuccess('fetchServer', 'strings', strings));
  } catch (error) {
    store.dispatch(organizationActions.fetchOrganizationFail());

    // Error Events
    const errorResponse = await prepareErrorMessage(null, error);
    ErrorReporter.captureException(error, 'error', {
      eventName: ERROR_MESSAGES.FETCH_BATCH_CONFIGS_ERROR,
      errorResponse,
    });
  }

  // Then call the page-level `getInitialProps` function that's wrapped by _app.
  const appInitialProps = await App.getInitialProps(context);

  // This only runs if the component is rendered on the server.
  if (context.ctx.req) {
    try {
      /**
       * Set the cache header to re-enable SRR page caching in Next@12+.
       * @see https://nextjs.org/docs/going-to-production#caching
       *
       * Store pages (/store/*) are excluded — they use a shorter TTL set in
       * next.config.mjs (s-maxage=60) so that deploy-time asset changes
       * reach diners within a minute rather than 24 hours (TEAM-14340).
       * `setHeader` replaces, so applying the default here would overwrite
       * the next.config.mjs value.
       */
      const requestUrl = context.ctx.req?.url ?? '';
      if (!requestUrl.startsWith('/store/')) {
        context.ctx.res?.setHeader('Cache-Control', DEFAULT_SSR_CACHE_POLICY);
      }
    } catch (error) {
      // setting the cache header can fail for 404 redirects
      Sentry.addBreadcrumb({ message: 'Failed to set cache header' });
      Sentry.captureException(error);
    }

    // Wait for all sagas to run before returning.
    store.dispatch(END);
    await (store as SagaStore).sagaTask?.toPromise();
  }

  /**
   * Return the page's initial props. If props from _app.getInitialProps, were
   * added, those would have to be spread onto appInitialProps.
   */
  return { ...appInitialProps, origin };
});

/**
 * Wrap the OrderingApp component in the `next-redux-wrapper`. Wrapping _app
 * will provide the store to the entire application and reconcile store state
 * across client- and server-side rendered components.
 *
 * One important note: when using `getInitialProps` or `getServerSideProps`,
 * you must use the versions exposed by the wrapper. Client-side components
 * can use `connect` or `useDispatch` / `useStore` as usual.
 *
 * @see https://nextjs.org/docs/advanced-features/custom-app
 * @see https://github.com/kirill-konshin/next-redux-wrapper#usage
 */

export default wrapper.withRedux(OrderingApp);
